const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

export type PostType = 'TEXT' | 'PHOTO' | 'VIDEO' | 'CONFESSION';
export type ConfessionMood = 'FUNNY' | 'SPICY' | 'SAD' | 'WHOLESOME' | 'RANDOM';

export interface FeedPost {
  id: string;
  type: PostType;
  content?: string | null;
  mediaData?: string | null;
  mediaType?: string | null;
  videoUrl?: string | null;
  mood?: ConfessionMood | null;
  createdAt: string;
  likeCount: number;
  commentCount: number;
  likedByMe: boolean;
  user: {
    id: string;
    gender?: 'MALE' | 'FEMALE' | 'OTHER' | null;
  };
}

export interface FeedComment {
  id: string;
  content: string;
  createdAt: string;
  user: {
    id: string;
    gender?: 'MALE' | 'FEMALE' | 'OTHER' | null;
  };
}

export interface Connection {
  userId: string;
  username: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER' | null;
  isVerified: boolean;
  lastChatAt: string;
  chatCount: number;
  isFollowing: boolean;
  isFollower: boolean;
  isFriend: boolean;
}

interface RequestOptions extends RequestInit {
  token?: string;
}

async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { token, ...rest } = options;

  const res = await fetch(`${API_URL}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...rest.headers,
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const firstDetail = Array.isArray(data.details) && data.details[0]?.message;
    const error = new Error(firstDetail || data.error || `HTTP ${res.status}`) as Error & {
      status: number;
      code?: string;
    };
    error.status = res.status;
    error.code = data.code;
    throw error;
  }

  // 204 No Content
  if (res.status === 204) return {} as T;

  return res.json();
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const api = {
  auth: {
    register: (data: { email: string; password: string; username?: string; gender?: string }) =>
      request('/api/auth/register', { method: 'POST', body: JSON.stringify(data) }),

    login: (data: { email: string; password: string }) =>
      request('/api/auth/login', { method: 'POST', body: JSON.stringify(data) }),

    anonymous: () =>
      request<{ sessionToken: string; userId: string }>('/api/auth/anonymous', { method: 'POST' }),

    me: (token: string) =>
      request('/api/auth/me', { token }),

    logout: (token: string) =>
      request('/api/auth/logout', { method: 'POST', token }),

    updateProfile: (data: { username?: string; gender?: string; profilePhoto?: string }, token: string) =>
      request('/api/auth/profile', { method: 'PATCH', body: JSON.stringify(data), token }),

    incrementConnections: (token: string) =>
      request<{ lifetimeConnections: number }>('/api/auth/me/connections', { method: 'POST', token }),
  },

  subscriptions: {
    createCheckout: (token: string) =>
      request<{ url: string }>('/api/subscriptions/checkout', { method: 'POST', body: JSON.stringify({}), token }),

    createPortal: (token: string) =>
      request<{ url: string }>('/api/subscriptions/portal', { token }),

    getStatus: (token: string) =>
      request('/api/subscriptions/status', { token }),

    cancel: (token: string) =>
      request('/api/subscriptions', { method: 'DELETE', token }),
  },

  reports: {
    create: (
      data: { reportedUserId: string; chatSessionId?: string; category: string; description?: string },
      token: string
    ) =>
      request('/api/reports', { method: 'POST', body: JSON.stringify(data), token }),
  },

  feed: {
    getPosts: (cursor?: string, token?: string) =>
      request<{ posts: FeedPost[]; nextCursor: string | null; hasMore: boolean }>(
        `/api/feed${cursor ? `?cursor=${cursor}` : ''}`,
        { token }
      ),
    createPost: (data: {
      type: PostType;
      content?: string;
      mediaData?: string;
      mediaType?: string;
      videoUrl?: string;
      mood?: ConfessionMood;
    }, token: string) =>
      request<{ post: FeedPost }>('/api/feed', {
        method: 'POST',
        body: JSON.stringify(data),
        token,
      }),
    deletePost: (id: string, token: string) =>
      request(`/api/feed/${id}`, { method: 'DELETE', token }),
    toggleLike: (id: string, token: string) =>
      request<{ likedByMe: boolean; likeCount: number }>(`/api/feed/${id}/like`, {
        method: 'POST',
        token,
      }),
    getMyPosts: (token: string, cursor?: string) =>
      request<{ posts: FeedPost[]; nextCursor: string | null; hasMore: boolean }>(
        `/api/feed/mine${cursor ? `?cursor=${cursor}` : ''}`,
        { token }
      ),
    getComments: (postId: string, cursor?: string) =>
      request<{ comments: FeedComment[]; nextCursor: string | null; hasMore: boolean }>(
        `/api/feed/${postId}/comments${cursor ? `?cursor=${cursor}` : ''}`
      ),
    addComment: (postId: string, content: string, token: string) =>
      request<{ comment: FeedComment }>(`/api/feed/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content }),
        token,
      }),
  },

  support: {
    createTicket: (data: { email: string; subject: string; message: string }) =>
      request('/api/support/tickets', { method: 'POST', body: JSON.stringify(data) }),

    getTicket: (id: string) =>
      request(`/api/support/tickets/${id}`),
  },

  connections: {
    getConnections: (token: string) =>
      request<{ connections: Connection[] }>('/api/connections', { token }),
    follow: (userId: string, token: string) =>
      request<{ success: boolean; isFriend: boolean }>(`/api/connections/follow/${userId}`, {
        method: 'POST',
        token,
      }),
    unfollow: (userId: string, token: string) =>
      request<{ success: boolean }>(`/api/connections/follow/${userId}`, { method: 'DELETE', token }),
  },

  messages: {
    getMessages: (friendId: string, token: string) =>
      request(`/api/messages/${friendId}`, { token }),
    getUnreadCount: (token: string) =>
      request<{ total: number; threads: Array<{ friendId: string; count: number }> }>(
        '/api/messages/unread/count',
        { token }
      ),
    markRead: (friendId: string, token: string) =>
      request<{ updated: number }>(`/api/messages/${friendId}/mark-read`, { method: 'POST', token }),
  },

  admin: {
    getStats: (token: string) =>
      request('/api/admin/stats', { token }),
    getUsers: (token: string, params?: string) =>
      request(`/api/admin/users${params ? `?${params}` : ''}`, { token }),
    getReports: (token: string, params?: string) =>
      request(`/api/reports${params ? `?${params}` : ''}`, { token }),
    reviewReport: (id: string, data: object, token: string) =>
      request(`/api/reports/${id}`, { method: 'PATCH', body: JSON.stringify(data), token }),
    getTickets: (token: string, params?: string) =>
      request(`/api/support/tickets${params ? `?${params}` : ''}`, { token }),
    banUser: (id: string, reason: string, token: string) =>
      request(`/api/admin/${id}/ban`, { method: 'PATCH', body: JSON.stringify({ reason }), token }),
    unbanUser: (id: string, token: string) =>
      request(`/api/admin/${id}/unban`, { method: 'PATCH', token }),
  },
};
